import {
  EnumFilter,
  ProviderFilters,
  SearchFilter,
  SelectFilter,
  WbResponsiveTableToolbar,
} from '@agilelab/plugin-wb-platform';
import { Environment } from '@agilelab/plugin-wb-platform-common';
import { FC } from 'react';
import useConsumableContext from './ConsumablesProvider';
import { ErrorPanel } from '@mainstage/core-components';

export interface ConsumablesFilters extends ProviderFilters {
  domain?: { name: string; value: string }[];
  system?: { name: string; value: string }[];
  environment?: Environment;
}

interface FilterProps {
  value: { name: string; value: string }[];
  changeFilters: <K extends keyof ConsumablesFilters>(
    key: K,
    value: ConsumablesFilters[K],
  ) => void;
  options: { name: string; value: string; disabled?: boolean }[];
}

interface EnvironmentFilterProps {
  value?: Environment;
  changeFilters: <K extends keyof ConsumablesFilters>(
    key: K,
    value: ConsumablesFilters[K],
  ) => void;
  options: Environment[];
}

const EnvironmentFilter: FC<EnvironmentFilterProps> = ({
  value,
  options,
  changeFilters,
}: EnvironmentFilterProps) => {
  return (
    <SelectFilter<Environment>
      field="Environment"
      options={options}
      value={value ?? options[0]}
      onChange={environment => {
        changeFilters('environment', environment ?? options[0]);
      }}
      renderOption={o => o.name}
      renderValue={o => o.name}
      onSearch={searchValue =>
        options.filter(o => new RegExp(searchValue, 'ig').test(o?.name))
      }
    />
  );
};

export const SystemFilter: FC<FilterProps> = ({
  value,
  changeFilters,
  options,
}) => {
  return (
    <EnumFilter<{ name: string; value: string; disabled?: boolean }>
      field="System"
      value={value ?? undefined}
      onChange={system => {
        changeFilters('system', system);
      }}
      renderOption={o => o.name}
      renderValue={v => v.name}
      onSearch={searchValue =>
        options.filter((o: { name: string; value: string }) =>
          new RegExp(searchValue, 'ig').test(o.name),
        )
      }
      options={options}
      isOptionDisabled={o => o.disabled ?? false}
    />
  );
};

const DomainFilter: FC<FilterProps> = ({ value, changeFilters, options }) => {
  return (
    <EnumFilter<{ name: string; value: string; disabled?: boolean }>
      field="Domain"
      value={value ?? undefined}
      onChange={domain => {
        changeFilters('domain', domain);
      }}
      renderOption={o => o.name}
      renderValue={v => v.name}
      onSearch={searchValue =>
        options.filter(o => new RegExp(searchValue, 'ig').test(o.name))
      }
      options={options}
      isOptionDisabled={o => o.disabled ?? false}
    />
  );
};

export const ConsumableFiltersSection = () => {
  const {
    filters,
    changeFilters,
    resetFilters,
    templateKind,
    availableEnvs,
    filtersContext,
  } = useConsumableContext();

  const error =
    filtersContext.availableValues.error || filtersContext.facetedValues.error;

  const loading =
    filtersContext.availableValues.loading ||
    filtersContext.facetedValues.loading;

  const getAvailableDomains = () => {
    return filtersContext?.enrichedValues?.Domain;
  };

  const getAvailableSystems = () => {
    return filtersContext?.enrichedValues?.Parent;
  };

  const renderFilter = (key: string) => {
    if (key === 'search') {
      return (
        <SearchFilter
          key={key}
          value={filters.search ?? ''}
          changeFilters={changeFilters}
        />
      );
    }

    if (key === 'domain') {
      return (
        <DomainFilter
          key={key}
          value={filters.domain ?? []}
          changeFilters={changeFilters}
          options={getAvailableDomains()}
        />
      );
    }

    if (key === 'system') {
      return (
        <SystemFilter
          key={key}
          value={filters.system ?? []}
          changeFilters={changeFilters}
          options={getAvailableSystems()}
        />
      );
    }

    if (key === 'environment') {
      return (
        <EnvironmentFilter
          key={key}
          value={filters.environment ?? undefined}
          changeFilters={changeFilters}
          options={availableEnvs ?? []}
        />
      );
    }

    return <></>;
  };

  if (error) return <ErrorPanel error={error} />;

  return loading ? (
    <></>
  ) : (
    <WbResponsiveTableToolbar onClear={resetFilters} marginBottom>
      <WbResponsiveTableToolbar.Filters>
        {templateKind?.userFilters?.map(filter => renderFilter(filter))}
      </WbResponsiveTableToolbar.Filters>
    </WbResponsiveTableToolbar>
  );
};
